/**
 * Report Controls Component
 * Date range selection and report generation controls
 */
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calendar, Settings, FileSpreadsheet } from "lucide-react";

export const ReportControls = ({
  reportType,
  setReportType,
  selectedMonth,
  setSelectedMonth,
  selectedYear,
  setSelectedYear,
  startDate,
  setStartDate,
  endDate,
  setEndDate,
  months,
  years,
  onGenerate,
  loading,
  showOperatingDaysConfig,
  setShowOperatingDaysConfig,
  language
}) => {
  const currentYear = new Date().getFullYear();
  const monthOptions = months || [];
  const yearOptions = years || Array.from({ length: 5 }, (_, i) => currentYear - 2 + i);

  return (
    <Card className="bg-white/80 backdrop-blur-sm border-2 border-blue-200">
      <CardHeader>
        <CardTitle className="text-xl text-blue-800 flex items-center gap-2">
          <Calendar className="w-5 h-5" />
          {language === 'es' ? 'Seleccionar Período' : 'Select Period'}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Report Type Selection */}
        <div className="flex gap-2 flex-wrap">
          <Button
            onClick={() => setReportType("month")}
            variant={reportType === "month" ? "default" : "outline"}
            className={reportType === "month" ? "bg-blue-600 text-white" : "border-blue-300 text-blue-700"}
          >
            Monthly
          </Button>
          <Button
            onClick={() => setReportType("year")}
            variant={reportType === "year" ? "default" : "outline"}
            className={reportType === "year" ? "bg-blue-600 text-white" : "border-blue-300 text-blue-700"}
          >
            Yearly
          </Button>
          <Button
            onClick={() => setReportType("custom")}
            variant={reportType === "custom" ? "default" : "outline"}
            className={reportType === "custom" ? "bg-blue-600 text-white" : "border-blue-300 text-blue-700"}
          >
            Custom Range
          </Button>
        </div>

        {/* Date Inputs */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {reportType === "month" && (
            <>
              <div>
                <Label className="text-blue-800">Month</Label>
                <Select value={selectedMonth.split("-")[1]} onValueChange={(m) => setSelectedMonth(`${selectedMonth.split("-")[0]}-${m}`)}>
                  <SelectTrigger className="border-blue-200">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {monthOptions.map(m => (
                      <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-blue-800">Year</Label>
                <Select value={selectedMonth.split("-")[0]} onValueChange={(y) => setSelectedMonth(`${y}-${selectedMonth.split("-")[1]}`)}>
                  <SelectTrigger className="border-blue-200">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {yearOptions.map(y => (
                      <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </>
          )}
          
          {reportType === "year" && (
            <div>
              <Label className="text-blue-800">Year</Label>
              <Select value={selectedYear} onValueChange={setSelectedYear}>
                <SelectTrigger className="border-blue-200">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {yearOptions.map(y => (
                    <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          
          {reportType === "custom" && (
            <>
              <div>
                <Label className="text-blue-800">Start Date</Label>
                <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="border-blue-200" />
              </div>
              <div>
                <Label className="text-blue-800">End Date</Label>
                <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="border-blue-200" />
              </div>
            </>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex gap-2 flex-wrap">
          <Button onClick={onGenerate} disabled={loading} className="bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white">
            <FileSpreadsheet className="w-4 h-4 mr-2" />
            {loading ? "Generating..." : "Generate Report"}
          </Button>
          <Button onClick={() => setShowOperatingDaysConfig(!showOperatingDaysConfig)} variant="outline" className="border-blue-300 text-blue-700">
            <Settings className="w-4 h-4 mr-2" />
            Operating Days
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default ReportControls;
